/* eslint-disable no-throw-literal */
import type { EmailValidationResponse, PhoneNumberValidationResponse } from '@eti/schema-types';
import type { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import type { Dispatch } from 'redux';
import {
  AGENCY_FORM_SECTION,
  CONTACT_INFO_FORM_SECTION,
  agencyFields,
  contactFields,
} from '../constants/formFields';
import { VALIDATE_EMAIL, VALIDATE_PHONE_NUMBER } from '../graphql/queries';
import type { Country, CustomerPhoneNumberDetailsType } from '../models';

const defaultResponse = { valid: true, message: '' };

const contactEmailField = `${CONTACT_INFO_FORM_SECTION}.${contactFields.EMAIL}`;
const contactPhoneField = `${CONTACT_INFO_FORM_SECTION}.${contactFields.PHONE}`;
const emergencyPhoneField = `${CONTACT_INFO_FORM_SECTION}.${contactFields.EMERGENCY_PHONE}`;
const agentEmailField = `${AGENCY_FORM_SECTION}.${agencyFields.AGENT_EMAIL}`;
const agentPhoneField = `${AGENCY_FORM_SECTION}.${agencyFields.AGENT_PHONE}`;
const agentPhoneCountryId = `${AGENCY_FORM_SECTION}.${agencyFields.AGENT_PHONE_COUNTRY_ID}`;

export const asyncBlurFields = [
  contactEmailField,
  contactPhoneField,
  emergencyPhoneField,
  agentEmailField,
  agentPhoneCountryId,
  agentPhoneField,
];

const validateEmail = (
  email: string,
  client: ApolloClient<NormalizedCacheObject>,
): Promise<EmailValidationResponse> =>
  new Promise<EmailValidationResponse>((resolve) => {
    client
      .query({ query: VALIDATE_EMAIL, variables: { email } })
      .then(({ data }) => {
        resolve(data?.validateEmail ?? defaultResponse);
      })
      .catch(() => {
        resolve(defaultResponse);
      });
  });

const validatePhoneNumber = (
  phoneNumberAfterPrefix: string,
  client: ApolloClient<NormalizedCacheObject>,
  prefixId?: string,
): Promise<PhoneNumberValidationResponse> =>
  new Promise<PhoneNumberValidationResponse>((resolve) => {
    if (prefixId) {
      client
        .query({ query: VALIDATE_PHONE_NUMBER, variables: { prefixId, phoneNumberAfterPrefix } })
        .then(({ data }) => {
          resolve(data?.validatePhoneNumber ?? defaultResponse);
        })
        .catch(() => {
          resolve(defaultResponse);
        });
    }
  });

const throwAsyncError = (
  formSection: string,
  formField: string,
  asyncErrors: any,
  message?: string | null,
) => {
  throw {
    ...asyncErrors,
    [formSection]: {
      ...(asyncErrors && asyncErrors[formSection] ? asyncErrors[formSection] : {}),
      [formField]: message ?? asyncErrors[formSection][formField],
    },
  };
};

const throwEmailError = (asyncErrors: any, message?: string | null) => {
  return throwAsyncError(CONTACT_INFO_FORM_SECTION, contactFields.EMAIL, asyncErrors, message);
};

const throwPhoneError = (asyncErrors: any, message?: string | null) => {
  return throwAsyncError(CONTACT_INFO_FORM_SECTION, contactFields.PHONE, asyncErrors, message);
};

const throwAgentEmailError = (asyncErrors: any, message?: string | null) => {
  return throwAsyncError(AGENCY_FORM_SECTION, agencyFields.AGENT_EMAIL, asyncErrors, message);
};

const throwAgentPhoneError = (asyncErrors: any, message?: string | null) => {
  return throwAsyncError(AGENCY_FORM_SECTION, agencyFields.AGENT_PHONE, asyncErrors, message);
};

const throwEmergencyPhoneError = (asyncErrors: any, message?: string | null) => {
  return throwAsyncError(
    CONTACT_INFO_FORM_SECTION,
    contactFields.EMERGENCY_PHONE,
    asyncErrors,
    message,
  );
};

const rethrowPreviousErrorsExcept = (fieldName: string, asyncErrors: any) => {
  if (
    asyncErrors[CONTACT_INFO_FORM_SECTION]?.[contactFields.PHONE] &&
    fieldName !== contactPhoneField
  ) {
    throwPhoneError(asyncErrors);
  }
  if (
    asyncErrors[CONTACT_INFO_FORM_SECTION]?.[contactFields.EMAIL] &&
    fieldName !== contactEmailField
  ) {
    throwEmailError(asyncErrors);
  }
  if (
    asyncErrors[CONTACT_INFO_FORM_SECTION]?.[contactFields.EMERGENCY_PHONE] &&
    fieldName !== emergencyPhoneField
  ) {
    throwEmergencyPhoneError(asyncErrors);
  }
  if (
    asyncErrors[AGENCY_FORM_SECTION]?.[agencyFields.AGENT_PHONE] &&
    fieldName !== agentPhoneField
  ) {
    throwAgentPhoneError(asyncErrors);
  }
  if (
    asyncErrors[AGENCY_FORM_SECTION]?.[agencyFields.AGENT_EMAIL] &&
    fieldName !== agentEmailField
  ) {
    throwAgentEmailError(asyncErrors);
  }
};

const getPrefixIdFromCountryId = (countryId: number, countries: Country[]) => {
  return countries.find((country) => country.id === countryId)?.phoneCountryId;
};

export const doAsyncFormValidation = async (
  values: any,
  _dispatch: Dispatch,
  props: {
    asyncErrors: any;
    client: ApolloClient<NormalizedCacheObject>;
    countries: Country[];
    setPhoneNumber: (arg: CustomerPhoneNumberDetailsType) => void;
  },
  fieldName: string,
) => {
  const { asyncErrors, client, countries, setPhoneNumber } = props;

  if (fieldName === contactPhoneField) {
    const phoneCountryId = values[CONTACT_INFO_FORM_SECTION][contactFields.PHONE_COUNTRY_ID];
    const prefixId = getPrefixIdFromCountryId(phoneCountryId, countries);

    const { message, valid } = await validatePhoneNumber(
      values[CONTACT_INFO_FORM_SECTION][contactFields.PHONE],
      client,
      prefixId,
    );

    if (!valid) {
      throwPhoneError(asyncErrors, message);
    } else if (
      asyncErrors &&
      (asyncErrors[CONTACT_INFO_FORM_SECTION] || asyncErrors[AGENCY_FORM_SECTION])
    ) {
      rethrowPreviousErrorsExcept(contactPhoneField, asyncErrors);
    } else {
      setPhoneNumber({
        customerPhoneNumber: values[CONTACT_INFO_FORM_SECTION][contactFields.PHONE],
        customerPhoneNumberPrefix: Number(phoneCountryId),
      });
    }
  }

  if (fieldName === contactEmailField) {
    const { message, valid } = await validateEmail(
      values[CONTACT_INFO_FORM_SECTION][contactFields.EMAIL],
      client,
    );

    if (!valid) {
      throwEmailError(asyncErrors, message);
    } else if (
      asyncErrors &&
      (asyncErrors[CONTACT_INFO_FORM_SECTION] || asyncErrors[AGENCY_FORM_SECTION])
    ) {
      rethrowPreviousErrorsExcept(contactEmailField, asyncErrors);
    }
  }

  if (fieldName === emergencyPhoneField) {
    const phoneCountryId =
      values[CONTACT_INFO_FORM_SECTION][contactFields.EMERGENCY_PHONE_COUNTRY_ID];
    const prefixId = getPrefixIdFromCountryId(phoneCountryId, countries);

    const { message, valid } = await validatePhoneNumber(
      values[CONTACT_INFO_FORM_SECTION][contactFields.EMERGENCY_PHONE],
      client,
      prefixId,
    );

    if (!valid) {
      throwEmergencyPhoneError(asyncErrors, message);
    } else if (
      asyncErrors &&
      (asyncErrors[CONTACT_INFO_FORM_SECTION] || asyncErrors[AGENCY_FORM_SECTION])
    ) {
      rethrowPreviousErrorsExcept(emergencyPhoneField, asyncErrors);
    }
  }

  if (fieldName === agentPhoneField || fieldName === agentPhoneCountryId) {
    const phoneCountryId = values[AGENCY_FORM_SECTION][agencyFields.AGENT_PHONE_COUNTRY_ID];
    const prefixId = getPrefixIdFromCountryId(phoneCountryId, countries);

    const { message, valid } = await validatePhoneNumber(
      values[AGENCY_FORM_SECTION][agencyFields.AGENT_PHONE],
      client,
      prefixId,
    );

    if (!valid) {
      throwAgentPhoneError(asyncErrors, message);
    } else if (
      asyncErrors &&
      (asyncErrors[CONTACT_INFO_FORM_SECTION] || asyncErrors[AGENCY_FORM_SECTION])
    ) {
      rethrowPreviousErrorsExcept(agentPhoneField, asyncErrors);
    }
  }

  if (fieldName === agentEmailField) {
    const { message, valid } = await validateEmail(
      values[AGENCY_FORM_SECTION][agencyFields.AGENT_EMAIL],
      client,
    );

    if (!valid) {
      throwAgentEmailError(asyncErrors, message);
    } else if (
      asyncErrors &&
      (asyncErrors[CONTACT_INFO_FORM_SECTION] || asyncErrors[AGENCY_FORM_SECTION])
    ) {
      rethrowPreviousErrorsExcept(agentEmailField, asyncErrors);
    }
  }
};
